import {useState, useEffect, useRef, useCallback} from "react";
import { createClient } from "@supabase/supabase-js";
import { sortMkts, buildInitialMonths, uid, getYearRange } from "../lib/calculations.js";
import { validateData, arr, obj, num } from "../lib/safeguards.js";
import { recomputeMarketplaceShares, recomputeProductShares } from "../lib/marketplaceShares.js";

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

export { supabase };

// ─── Save status taxonomy ────────────────────────────────────────────────
// Single source of truth for the save indicator across the app. Consumers
// (FieldWrap, Info, App top-bar) import these maps instead of duplicating
// them — adding a new status here propagates everywhere.
export const SAVE_STATUS_LABELS = {
  saved: "Saved",
  saving: "Saving…",
  offline: "Offline — cached locally",
  error: "Save failed",
  auth_expired: "Session expired — refresh the page to continue saving",
  rate_limited: "Too many saves — pausing briefly",
  server_error: "Server error — retrying…",
  timeout: "Save timed out — check your connection",
  payload_too_large: "Brand data too large to save — contact support",
};
export const SAVE_STATUS_COLORS = {
  saved: "#34c759",
  saving: "#ff9f0a",
  offline: "#8e8e93",
  error: "#ff3b30",
  auth_expired: "#ff3b30",
  rate_limited: "#ff9f0a",
  server_error: "#ff3b30",
  timeout: "#ff3b30",
  payload_too_large: "#ff3b30",
};
export const SAVE_STATUS_IS_ERROR = (s) =>
  s != null && s !== "saved" && s !== "saving" && s !== "offline";

// If the access token expires within this many seconds, proactively refresh
// before sending. Stops the "expired mid-flight" failure on slow uploads.
const TOKEN_REFRESH_LEEWAY_SECONDS = 60;
const SAVE_FETCH_TIMEOUT_MS = 30_000;
const KPI_HISTORY_TIMEOUT_MS = 15_000;

async function getFreshAccessToken() {
  try {
    const { data } = await supabase.auth.getSession();
    let session = data?.session;
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session?.expires_at ?? 0;
    if (!session || expiresAt - now < TOKEN_REFRESH_LEEWAY_SECONDS) {
      const { data: refreshed, error } = await supabase.auth.refreshSession();
      if (error) {
        console.warn("[useData] refreshSession failed:", error.message || error);
        return null;
      }
      session = refreshed?.session ?? session;
    }
    return session?.access_token ?? null;
  } catch (err) {
    console.warn("[useData] getFreshAccessToken threw:", err?.message || err);
    return null;
  }
}

function mapResponseToStatus(res) {
  if (res.ok) return "saved";
  if (res.status === 401 || res.status === 403) return "auth_expired";
  if (res.status === 413) return "payload_too_large";
  if (res.status === 429) return "rate_limited";
  if (res.status >= 500) return "server_error";
  return "error";
}

// ─── Default data ─────────────────────────────────────────────────────────────
const TODAY = new Date();

// Default marketplaces for a fresh account.
// All start at pctOfTotal: 0 with no products. The first marketplace the user
// adds data to will auto-resolve to 100% share via marketplaceUtils.recomputeMarketplaceShares.
// Empty default — fresh users see the "No marketplaces yet · Add first
// marketplace" empty-state CTA on the Marketplaces page (Marketplaces.jsx
// line ~2249). Previously this seeded 4 ghost cards (Amazon / Shopify /
// Walmart / eBay) which made the app look pre-set-up and clashed with
// the "Coming soon" labelling on non-live integrations (fix #4). Existing
// users keep whatever's in the server payload — only the pre-fetch
// placeholder shape changes.
const DEFAULT_MARKETPLACES = [];

// ─── Main data hook ───────────────────────────────────────────────────────────
export function useData(session, dataKey = "main", dataOwnerId = null) {
  // dataOwnerId: whose data to load/save. For owners this is their own id.
  // For invited members, this is the account owner's id (shared data).
  const effectiveUserId = dataOwnerId || session?.user?.id;
  const [marketplaces, setMarketplaces] = useState(DEFAULT_MARKETPLACES);
  const [months, setMonths] = useState(() => buildInitialMonths(TODAY));
  const [recurringIncome, setRecurringIncome] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [deliveryCreditLimit, setDeliveryCreditLimitState] = useState(0);
  const [orders, setOrders] = useState([]);
  const [fbmSettings, setFbmSettingsState] = useState([]);
  const [partners, setPartnersState] = useState([]);
  const [modifiers, setModifiersState] = useState([]);
  const [decisions, setDecisionsState] = useState([
    { id: "example_decision", name: "EXAMPLE — Please delete", category: "product", status: "planned", date: new Date(new Date().getFullYear(), new Date().getMonth()+1, 15).toISOString().slice(0,10), impacts: [], notes: "This is a sample decision to show you how the Planner works. Click Edit to see how decisions are structured, then delete this when you're ready." }
  ]);
  const [taxes, setTaxesState] = useState([]);
  const [profitCards, setProfitCardsState] = useState([]);
  const [incomeOverrides, setIncomeOverrides] = useState({});
  const [availableToday, setAvailableTodayState] = useState(0);
  const [availableTodayHistory, setAvailableTodayHistoryState] = useState([]);
  const [initialStock, setInitialStockState] = useState(0);
  const [initialCostPerUnit, setInitialCostPerUnitState] = useState(0);
  const [initialStockDate, setInitialStockDateState] = useState("");
  const [initialStockHistory, setInitialStockHistoryState] = useState([]);
  const [inventoryPools, setInventoryPoolsState] = useState([]);
  const [visibleYears, setVisibleYearsState] = useState(() => {
    const { currentYear, nextYear } = getYearRange(TODAY);
    return [currentYear, nextYear];
  });
  const [saveStatus, setSaveStatus] = useState("saved");
  // Past balance snapshots — Map<"YYYY-MM-DD", balance>. Populated on load
  // by fetching /api/kpi-history (kpi_id=todayBalance). Consumed by the
  // Dashboard chart to freeze the purple balance line for past dates.
  // Empty Map means no history yet; the chart falls back to computed values.
  const [pastBalances, setPastBalances] = useState(() => new Map());
  // Brand UUID for this dataKey — set after the first successful reload().
  // Used to scope the Realtime subscription below so we only get events
  // for this brand, not every brand in the project.
  const [brandId, setBrandId] = useState(null);
  // Auto-detect base currency from the user's browser locale on first run.
  // Maps the locale's region code (e.g. "en-DE" → "DE") to its ISO 4217
  // currency. Falls back to USD for unknown / non-mainstream locales.
  // Server later overwrites this with the persisted value when the brand
  // loads (line ~112), so this only affects fresh accounts.
  const [currency, setCurrencyState] = useState(() => {
    try {
      const loc = (typeof Intl !== "undefined" && Intl.NumberFormat
        ? Intl.NumberFormat().resolvedOptions().locale
        : (navigator?.language || "en-US"));
      const region = (loc.split("-")[1] || "").toUpperCase();
      // Common seller markets we explicitly target. Anything else → USD.
      const map = {
        US:"USD", CA:"CAD", GB:"GBP", AU:"AUD", NZ:"NZD",
        DE:"EUR", FR:"EUR", ES:"EUR", IT:"EUR", NL:"EUR", BE:"EUR",
        AT:"EUR", PT:"EUR", IE:"EUR", FI:"EUR", GR:"EUR", LU:"EUR",
        SK:"EUR", SI:"EUR", EE:"EUR", LV:"EUR", LT:"EUR", MT:"EUR",
        CY:"EUR", HR:"EUR",
        CH:"CHF", SE:"SEK", NO:"NOK", DK:"DKK", PL:"PLN", CZ:"CZK", HU:"HUF",
        RO:"RON", BG:"BGN",
        JP:"JPY", CN:"CNY", IN:"INR", SG:"SGD", HK:"HKD", KR:"KRW",
        MX:"MXN", BR:"BRL", AR:"ARS", CL:"CLP",
        IL:"ILS", AE:"AED", SA:"SAR", TR:"TRY", ZA:"ZAR",
      };
      return map[region] || "USD";
    } catch {
      return "USD";
    }
  });
  const [unitSystem, setUnitSystemState] = useState("metric");
  const [weekStartDay, setWeekStartDayState] = useState(1); // 0=Sun,1=Mon,2=Tue,3=Wed,4=Thu,5=Fri,6=Sat
  const [dataLoading, setDataLoading] = useState(true);
  // loadError surfaces a fetch failure on /api/brand-state so the UI can
  // show a "Retry" card instead of silently rendering empty defaults as
  // if nothing went wrong. null when everything's fine.
  const [loadError, setLoadError] = useState(null);
  const saveTimer = useRef(null);
  const pendingMergeRef = useRef(null);
  const firstTimeBackfillRef = useRef(false);

  // ── Load from Supabase with localStorage cache ──
  const CACHE_VERSION = 2; // Increment when data structure changes
  const cacheKey = `sf_cache_${effectiveUserId}_${dataKey}`;

  function applyData(raw) {
    if (!raw) return;
    // Validate and sanitize data shapes before applying to state
    const p = validateData(raw);
    // Marketplaces: only overwrite if the server actually has at least one.
    // Empty array means a fresh brand — keep DEFAULT_MARKETPLACES from the reset.
    if (Array.isArray(p.marketplaces) && p.marketplaces.length > 0) {
      // Decouple safety net — layer localStorage overlay on top of the DB
      // values so the flag survives even if both the `decoupled` column
      // write and the apiConnection JSONB overlay somehow fail. The UI
      // toggles this overlay synchronously on every Decouple click.
      let overlay = {};
      try { overlay = JSON.parse(localStorage.getItem(`ch.decouple.${dataKey}`) || "{}") || {}; } catch {}
      const mktsWithOverlay = p.marketplaces.map(m => ({
        ...m,
        products: (m.products || []).map(pr => {
          const key = `${m.id}:${pr.id}`;
          // Overlay is a FALLBACK only when the DB has no opinion. The
          // server's loadBrand always sets _decoupled to a boolean
          // (true / false) from the `decoupled` column + the
          // apiConnection overlay, so we treat any explicit boolean as
          // authoritative. Only apply the LS overlay when the field is
          // genuinely missing — happens for legacy rows that predate
          // the columns. Without this guard, a stale "decoupled=true"
          // overlay entry would resurrect on every reload even after
          // the user toggled sync back ON.
          if (typeof pr._decoupled === "boolean") return pr;
          return overlay[key] ? { ...pr, _decoupled: true } : pr;
        }),
      }));
      setMarketplaces(sortMkts(mktsWithOverlay));
    }
    if (p.months) setMonths({ ...buildInitialMonths(TODAY), ...obj(p.months) });
    if (p.recurringIncome) setRecurringIncome(arr(p.recurringIncome));
    if (p.recurringExpenses) setRecurringExpenses(arr(p.recurringExpenses));
    if (p.deliveryCreditLimit !== undefined) setDeliveryCreditLimitState(num(p.deliveryCreditLimit));
    if (p.orders) setOrders(arr(p.orders));
    if (p.fbmSettings) setFbmSettingsState(arr(p.fbmSettings));
    if (p.partners) setPartnersState(arr(p.partners));
    if (p.modifiers) setModifiersState(arr(p.modifiers));
    if (p.decisions) setDecisionsState(arr(p.decisions));
    if (p.taxes) setTaxesState(arr(p.taxes));
    if (p.profitCards) setProfitCardsState(arr(p.profitCards));
    if (p.incomeOverrides) setIncomeOverrides(obj(p.incomeOverrides));
    if (p.availableToday !== undefined) setAvailableTodayState(num(p.availableToday));
    if (p.availableTodayHistory) setAvailableTodayHistoryState(arr(p.availableTodayHistory));
    if (p.initialStock !== undefined) setInitialStockState(num(p.initialStock));
    if (p.initialCostPerUnit !== undefined) setInitialCostPerUnitState(num(p.initialCostPerUnit));
    if (p.initialStockDate) setInitialStockDateState(p.initialStockDate);
    if (p.initialStockHistory) setInitialStockHistoryState(arr(p.initialStockHistory));
    if (p.inventoryPools) setInventoryPoolsState(arr(p.inventoryPools));
    // Only overwrite visibleYears if the server has at least one year — empty
    // would zero out the planner timeline (slots.length=0 → 0-width axis).
    if (Array.isArray(p.visibleYears) && p.visibleYears.length > 0) setVisibleYearsState(arr(p.visibleYears));
    if (p.currency) setCurrencyState(p.currency);
    if (p.unitSystem) setUnitSystemState(p.unitSystem);
    if (p.weekStartDay !== undefined) setWeekStartDayState(num(p.weekStartDay, 1));
    // One-time backfill: existing paid:true rows without paidAt — stamp with the record's own date
    try { backfillPaidAt(p); } catch {}
    // One-time migration: when delivery-cost stopped being a Cash Calendar expense,
    // existing products with deliveryCost > 0 must opt-in to `deductDeliveryFromPayout`
    // so they don't silently lose the deduction (otherwise their projected payout would inflate).
    try { migrateDeliveryFlag(p); } catch {}
  }

  // ── Migration: flip deductDeliveryFromPayout = true on legacy products with deliveryCost > 0 ──
  function migrateDeliveryFlag(p) {
    if (!Array.isArray(p?.marketplaces)) return;
    if (p?.months?._appSettings?.deliveryFlagMigrated) return;
    let changed = false;
    p.marketplaces = p.marketplaces.map(m => {
      if (!Array.isArray(m?.products)) return m;
      const products = m.products.map(prod => {
        if (Number(prod?.deliveryCost) > 0 && prod.deductDeliveryFromPayout === undefined) {
          changed = true;
          return { ...prod, deductDeliveryFromPayout: true };
        }
        return prod;
      });
      return { ...m, products };
    });
    const prevSettings = p.months?._appSettings || {};
    p.months = { ...(p.months || {}), _appSettings: { ...prevSettings, deliveryFlagMigrated: true } };
    if (!changed) return;
  }

  // ── Backfill paidAt for legacy paid:true entries (runs once per dataset) ──
  function backfillPaidAt(p) {
    if (!p?.months || p.months._appSettings?.paidAtBackfilled) return;
    let changed = false;
    const monthsOut = { ...p.months };
    for (const [mKey, md] of Object.entries(monthsOut)) {
      if (mKey === "_appSettings" || !md || typeof md !== "object") continue;
      // expenses array (manual)
      if (Array.isArray(md.expenses)) {
        const patched = md.expenses.map(e => {
          if (e?.paid && !e.paidAt) {
            changed = true;
            const d = e.date ? new Date(e.date) : null;
            return { ...e, paidAt: (d && !isNaN(d)) ? d.toISOString() : new Date().toISOString() };
          }
          return e;
        });
        monthsOut[mKey] = { ...md, expenses: patched };
      }
      // deliveryOverrides
      if (md.deliveryOverrides && typeof md.deliveryOverrides === "object") {
        const patched = { ...md.deliveryOverrides };
        for (const [k, v] of Object.entries(patched)) {
          if (v?.paid && !v.paidAt) { changed = true; patched[k] = { ...v, paidAt: new Date().toISOString() }; }
        }
        monthsOut[mKey] = { ...monthsOut[mKey], deliveryOverrides: patched };
      }
      // recurringOverrides
      if (md.recurringOverrides && typeof md.recurringOverrides === "object") {
        const patched = { ...md.recurringOverrides };
        for (const [k, v] of Object.entries(patched)) {
          if (v?.paid && !v.paidAt) { changed = true; patched[k] = { ...v, paidAt: new Date().toISOString() }; }
        }
        monthsOut[mKey] = { ...monthsOut[mKey], recurringOverrides: patched };
      }
      // orderExpPaid + orderExpPaidAt (parallel maps)
      if (md.orderExpPaid && typeof md.orderExpPaid === "object") {
        const paidAtMap = { ...(md.orderExpPaidAt || {}) };
        let anyAdded = false;
        for (const [k, paid] of Object.entries(md.orderExpPaid)) {
          if (paid && !paidAtMap[k]) { paidAtMap[k] = new Date().toISOString(); anyAdded = true; }
        }
        if (anyAdded) { changed = true; monthsOut[mKey] = { ...monthsOut[mKey], orderExpPaidAt: paidAtMap }; }
      }
    }
    if (changed) {
      const prevSettings = monthsOut._appSettings || {};
      monthsOut._appSettings = { ...prevSettings, paidAtBackfilled: true };
      // Write back to state via the setter — applyData will use this updated months
      p.months = monthsOut;
    } else if (!p.months._appSettings?.paidAtBackfilled) {
      // Even if nothing changed, mark it so we don't retry every load
      const prevSettings = p.months._appSettings || {};
      p.months = { ...p.months, _appSettings: { ...prevSettings, paidAtBackfilled: true } };
    }
  }

  function saveCache(p) {
    try { localStorage.setItem(cacheKey, JSON.stringify({ v: CACHE_VERSION, t: Date.now(), d: p })); } catch {}
  }

  function loadCache() {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.v !== CACHE_VERSION) { localStorage.removeItem(cacheKey); return null; }
      return parsed.d;
    } catch { return null; }
  }

  // Refetch brand state from the server. Used by the initial load effect AND
  // exposed publicly so callers (e.g. post-OAuth marketplace sync) can force a
  // fresh pull without remounting the hook.
  const reload = useCallback(async ({ skipReset = false } = {}) => {
    if (!session) return;
    if (!skipReset) {
      // Don't show the loading screen on a manual reload — keep UI stable
      setDataLoading(true);
    }
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      const token = s?.access_token;
      if (!token) {
        console.error("No auth token available");
        return;
      }
      const res = await fetch(`/api/brand-state?key=${encodeURIComponent(dataKey)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text();
        console.error("Failed to load brand state:", res.status, body);
        setLoadError(new Error(`HTTP ${res.status}`));
        return;
      }
      // Success clears any prior error so the retry banner goes away.
      setLoadError(null);
      const payload = await res.json();
      const p = payload?.state;
      if (payload?.brandId) setBrandId(payload.brandId);
      if (p) {
        applyData(p);
        saveCache(p);
      }

      // Past balance snapshots — fire-and-forget, non-blocking. Read up to
      // 365 days of todayBalance history; Dashboard uses it to freeze the
      // purple line for past dates. Failure is silent: empty Map means the
      // chart falls back to computed values (current behavior).
      fetch(`/api/kpi-history?key=${encodeURIComponent(dataKey)}&kpi_id=todayBalance&days=365`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(KPI_HISTORY_TIMEOUT_MS),
      })
        .then(r => {
          if (!r.ok) {
            // 401 here is the canary for "session went stale while we were
            // sitting on the page" — log it so we can correlate with save
            // failures in the same window instead of swallowing silently.
            console.warn(`[useData] kpi-history fetch failed (${r.status})`);
            return null;
          }
          return r.json();
        })
        .then(body => {
          if (!body?.snapshots) return;
          const map = new Map();
          for (const s of body.snapshots) {
            if (s?.date && s.value != null) map.set(s.date, Number(s.value));
          }
          setPastBalances(map);
        })
        .catch(err => {
          console.warn("[useData] kpi-history fetch error:", err?.message || err);
        });

      // First-time backfill: if the server has no marketplaces yet, the user
      // is fresh and applyData kept the local DEFAULT_MARKETPLACES. Persist
      // them to the server immediately so the next reload doesn't see empty.
      if (!p?.marketplaces || (Array.isArray(p.marketplaces) && p.marketplaces.length === 0)) {
        // Fire-and-forget — saveDefaultsRef will be called by the save effect
        firstTimeBackfillRef.current = true;
      }

      // Merge any pending decisions from cross-brand sharing
      try {
        const pendingKey = `sf_pending_decisions_${dataKey}`;
        const pending = JSON.parse(localStorage.getItem(pendingKey) || "[]");
        if (pending.length > 0) {
          const currentDecs = p?.decisions || [];
          const merged = [...currentDecs];
          for (const pd of pending) {
            const existIdx = merged.findIndex(d => d.id === pd.id);
            if (existIdx >= 0) merged[existIdx] = pd;
            else merged.push(pd);
          }
          setDecisionsState(merged);
          localStorage.removeItem(pendingKey);
          pendingMergeRef.current = merged;
        }
      } catch {}
    } catch (err) {
      console.error("Unexpected error loading brand state:", err);
      setLoadError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setDataLoading(false);
    }
  }, [session?.user?.id, dataKey]);

  useEffect(() => {
    if (!session) return;

    // Reset to defaults when switching brands
    setMarketplaces(DEFAULT_MARKETPLACES);
    setMonths(buildInitialMonths(TODAY));
    setRecurringIncome([]);
    setRecurringExpenses([]);
    setDeliveryCreditLimitState(0);
    setOrders([]);
    setFbmSettingsState([]);
    setPartnersState([]);
    setModifiersState([]);
    setTaxesState([]);
    setIncomeOverrides({});
    setAvailableTodayState(0);
    setInitialStockState(0);
    setInitialCostPerUnitState(0);
    setInitialStockDateState("");
    setInitialStockHistoryState([]);
    setVisibleYearsState([getYearRange(TODAY).currentYear, getYearRange(TODAY).nextYear]);
    setSaveStatus("saved");

    // 1. Apply cached data instantly (no loading screen for returning users)
    const cached = loadCache();
    if (cached) {
      applyData(cached);
      setDataLoading(false); // Cache applied — skip loading screen
    } else {
      setDataLoading(true); // No cache — show skeleton until Supabase responds
    }

    // 2. Always fetch fresh data from server (uses the public reload function)
    reload({ skipReset: true });

    return () => {};
  }, [session?.user?.id, dataKey, effectiveUserId]);

  // ── Live multi-user sync via Supabase Realtime ──────────────────────────
  // When another user on the same account saves, their API call touches
  // brand_settings.updated_at. We subscribe to that table filtered by our
  // brandId — the callback silently refetches so this tab's state mirrors
  // the latest server state within ~1s, no refresh needed.
  //
  // Requires ALTER PUBLICATION supabase_realtime ADD TABLE public.brand_settings
  // on the DB side (one-time setup in Supabase SQL Editor or Database →
  // Publications UI). Without it, the subscription is a silent no-op.
  useEffect(() => {
    if (!session?.user?.id || !brandId) return;
    const channel = supabase
      .channel(`brand-sync-${brandId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "brand_settings",
          filter: `brand_id=eq.${brandId}`,
        },
        () => {
          // Silent refresh — other tabs may have just saved.
          reload({ skipReset: true });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [session?.user?.id, brandId, reload]);

  // ── Save to Supabase ──
  function getFullState(overrides = {}) {
    return {
      marketplaces: overrides.mk ?? marketplaces,
      months: overrides.mo ?? months,
      recurringIncome: overrides.ri ?? recurringIncome,
      recurringExpenses: overrides.re ?? recurringExpenses,
      deliveryCreditLimit: overrides.dcl ?? deliveryCreditLimit,
      orders: overrides.ord ?? orders,
      fbmSettings: overrides.fbm ?? fbmSettings,
      partners: overrides.par ?? partners,
      modifiers: overrides.mod ?? modifiers,
      decisions: overrides.dec ?? decisions,
      taxes: overrides.tax ?? taxes,
      profitCards: overrides.pc ?? profitCards,
      incomeOverrides: overrides.io ?? incomeOverrides,
      availableToday: overrides.at ?? availableToday,
      availableTodayHistory: overrides.ath ?? availableTodayHistory,
      initialStock: overrides.is ?? initialStock,
      initialCostPerUnit: overrides.icpu ?? initialCostPerUnit,
      initialStockDate: overrides.isd ?? initialStockDate,
      initialStockHistory: overrides.ish ?? initialStockHistory,
      inventoryPools: overrides.ipools ?? inventoryPools,
      visibleYears: overrides.vy ?? visibleYears,
      currency: overrides.cur ?? currency,
      unitSystem: overrides.us ?? unitSystem,
      weekStartDay: overrides.wsd ?? weekStartDay,
    };
  }

  const pendingState = useRef(null); // latest state snapshot for debounced save
  const retryTimer  = useRef(null);  // retry after a failed save

  async function _doSave(snapshot) {
    try {
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        setSaveStatus("offline");
        pendingState.current = { ...(pendingState.current || {}), ...snapshot };
        try { saveCache(getFullState(snapshot)); } catch {}
        return;
      }

      const token = await getFreshAccessToken();
      if (!token) {
        // Auth refresh failed → session is dead. Don't send Bearer undefined.
        setSaveStatus("auth_expired");
        pendingState.current = { ...(pendingState.current || {}), ...snapshot };
        try { saveCache(getFullState(snapshot)); } catch {}
        return;
      }
      const fullState = getFullState(snapshot);
      const res = await fetch("/api/brand-state", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ key: dataKey, state: fullState }),
        signal: AbortSignal.timeout(SAVE_FETCH_TIMEOUT_MS),
      });

      if (!res.ok) {
        const status = mapResponseToStatus(res);
        let detail = "";
        try { detail = (await res.clone().text()).slice(0, 800); } catch {}
        console.error(`[useData] save failed (${res.status} → ${status}):`, detail);
        setSaveStatus(status);
        pendingState.current = { ...(pendingState.current || {}), ...snapshot };
        // Only retry transient failures. auth_expired needs a fresh login,
        // payload_too_large needs user intervention, rate_limited needs a
        // longer cooldown than 5s — retrying any of those just spams.
        if (status === "server_error" || status === "error") {
          clearTimeout(retryTimer.current);
          retryTimer.current = setTimeout(async () => {
            const retrySnapshot = pendingState.current || {};
            pendingState.current = null;
            try {
              const t2 = await getFreshAccessToken();
              if (!t2) { setSaveStatus("auth_expired"); return; }
              const r2 = await fetch("/api/brand-state", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${t2}` },
                body: JSON.stringify({ key: dataKey, state: getFullState(retrySnapshot) }),
                signal: AbortSignal.timeout(SAVE_FETCH_TIMEOUT_MS),
              });
              setSaveStatus(r2.ok ? "saved" : mapResponseToStatus(r2));
              if (r2.ok) { try { saveCache(getFullState(retrySnapshot)); } catch {} }
            } catch (err) {
              if (err?.name === "TimeoutError" || err?.name === "AbortError") {
                setSaveStatus("timeout");
              } else {
                setSaveStatus("error");
              }
              console.error("[useData] save retry threw:", err?.message || err);
            }
          }, 5000);
        }
      } else {
        setSaveStatus("saved");
        try { saveCache(fullState); } catch {}
      }
    } catch (err) {
      if (err?.name === "TimeoutError" || err?.name === "AbortError") {
        console.error("[useData] save timed out after", SAVE_FETCH_TIMEOUT_MS, "ms");
        setSaveStatus("timeout");
      } else {
        console.error("[useData] save network error:", err?.message || err);
        setSaveStatus("error");
      }
      try { saveCache(getFullState(snapshot)); } catch {}
      pendingState.current = { ...(pendingState.current || {}), ...snapshot };
    }
  }

  // Flush pending saves when coming back online
  useEffect(() => {
    function handleOnline() {
      if (pendingState.current) {
        setSaveStatus("saving");
        const snapshot = pendingState.current;
        pendingState.current = null;
        _doSave(snapshot);
      }
    }
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, []);

  function save(overrides = {}) {
    // Merge overrides into pending snapshot so rapid saves always write latest values
    pendingState.current = { ...(pendingState.current || {}), ...overrides };
    setSaveStatus("saving");
    clearTimeout(saveTimer.current);
    clearTimeout(retryTimer.current);
    saveTimer.current = setTimeout(async () => {
      const snapshot = pendingState.current || {};
      pendingState.current = null;
      await _doSave(snapshot);
    }, 900); // 900ms debounce — enough for rapid typing to settle
  }

  // Save merged pending decisions after hook is fully initialized
  useEffect(() => {
    if (!dataLoading && pendingMergeRef.current) {
      const merged = pendingMergeRef.current;
      pendingMergeRef.current = null;
      save({ dec: merged });
    }
  }, [dataLoading]);

  // First-time backfill: server had no marketplaces — persist DEFAULT_MARKETPLACES
  // so the next reload doesn't show the user as having zero marketplaces.
  useEffect(() => {
    if (!dataLoading && firstTimeBackfillRef.current && marketplaces.length > 0) {
      firstTimeBackfillRef.current = false;
      save({ mk: marketplaces });
    }
  }, [dataLoading, marketplaces]);

  // ── Marketplace operations ──
  // Write the decouple overlay to localStorage on every setMarketplacesAll
  // call. Read back on applyData so the flag survives even if the DB save
  // fails entirely (network error, permissions, stale schema, anything).
  function _persistDecoupleOverlay(u) {
    try {
      const overlay = {};
      (u || []).forEach(m => {
        (m.products || []).forEach(p => {
          if (p._decoupled) overlay[`${m.id}:${p.id}`] = true;
        });
      });
      localStorage.setItem(`ch.decouple.${dataKey}`, JSON.stringify(overlay));
    } catch {}
  }
  const mktOps = {
    setMarketplacesAll: (u) => { _persistDecoupleOverlay(u); setMarketplaces(u); save({ mk: u }); },
    update: (id, patch) => {
      const u = sortMkts(marketplaces.map(m => m.id === id ? { ...m, ...patch } : m));
      setMarketplaces(u); save({ mk: u });
    },
    add: () => {
      const id = uid();
      const u = sortMkts([...marketplaces, { id, name: "New Marketplace", canDelete: true, pctOfTotal: 0, payoutDelay: 7, products: [] }]);
      setMarketplaces(u); save({ mk: u });
    },
    addWithConfig: (name, activeFrom, products, icon, color) => {
      const id = uid();
      const u = sortMkts([...marketplaces, { id, name, canDelete: true, pctOfTotal: 0, payoutDelay: 7, activeFrom: activeFrom || undefined, products: products || [], icon: icon || "store", color: color || undefined }]);
      setMarketplaces(u); save({ mk: u });
    },
    delete: (id) => {
      setMarketplaces(prev => {
        const u = recomputeMarketplaceShares(prev.filter(m => m.id !== id));
        save({ mk: u });
        return u;
      });
      // Clean up pool references — remove deleted marketplace from all pools
      const cleanedPools = inventoryPools.map(p => {
        const ids = p.marketplaceIds || [];
        if (!ids.includes(id)) return p;
        return { ...p, marketplaceIds: ids.filter(mid => mid !== id) };
      });
      if (cleanedPools.some((p, i) => p !== inventoryPools[i])) {
        setInventoryPoolsState(cleanedPools); save({ ipools: cleanedPools });
      }
    },
    addProduct: (mktId, productData = {}) => {
      const id = uid();
      const u = marketplaces.map(m => m.id === mktId ? {
        ...m, products: [...m.products, { id, name: "New Product", pctOfMarket: 0, payout: 0, deliveryCost: 0, ...productData }]
      } : m);
      setMarketplaces(u); save({ mk: u });
      return id;
    },
    updateProduct: (mktId, prodId, patch) => {
      const u = marketplaces.map(m => m.id === mktId ? {
        ...m, products: m.products.map(p => p.id === prodId ? { ...p, ...patch } : p)
      } : m);
      // If the patch touches _decoupled, refresh the localStorage overlay
      // too. Otherwise a stale "decoupled=true" entry from a previous
      // toggle keeps resurrecting on the next reload (see applyData).
      // Without this, toggling sync ON wrote decoupled=false to the DB
      // but the overlay still re-applied true on the very next page load.
      if (Object.prototype.hasOwnProperty.call(patch, "_decoupled")) {
        _persistDecoupleOverlay(u);
      }
      setMarketplaces(u); save({ mk: u });
    },
    deleteProduct: (mktId, prodId) => {
      setMarketplaces(prev => {
        const updated = prev.map(m => {
          if (m.id !== mktId) return m;
          const remaining = recomputeProductShares(m.products.filter(p => p.id !== prodId));
          const patch = { ...m, products: remaining };
          // Unlock pctOfTotal if no active products left
          if (remaining.filter(p => !p._decoupled).length === 0) {
            patch._overridden = { ...(m._overridden || {}), pctOfTotal: false };
            patch.pctOfTotal = 0;
          }
          return patch;
        });
        const u = recomputeMarketplaceShares(updated);
        save({ mk: u });
        return u;
      });
    },
  };

  // ── Month operations ──
  const monthOps = {
    setMonthsDirect: (u) => { setMonths(u); save({ mo: u }); },
    // Functional variant — safe from stale closures when called rapidly
    updateMonth: (key, patch) => {
      setMonths(prev => {
        const next = { ...prev, [key]: { ...(prev[key] || {}), ...(typeof patch === "function" ? patch(prev[key] || {}) : patch) } };
        save({ mo: next });
        return next;
      });
    },
    updateTarget: (key, val) => {
      setMonths(prev => {
        const parsed = parseInt(val) || 0;
        const oldEntry = prev[key] || {};
        const oldTarget = Number(oldEntry.target) || 0;
        // Track every change to the target as a history entry so the
        // Targets page can render a small timeline chart per month. Cap
        // at 50 entries (oldest dropped). Skip when the value is the
        // same as the prior — avoids noise from focus-out events that
        // re-write the unchanged number.
        let history = Array.isArray(oldEntry.targetHistory) ? oldEntry.targetHistory : [];
        if (parsed !== oldTarget) {
          history = [
            { value: parsed, ts: new Date().toISOString() },
            ...history.slice(0, 49),
          ];
        }
        const next = { ...prev, [key]: { ...oldEntry, target: parsed, targetHistory: history } };
        save({ mo: next });
        return next;
      });
    },
    updateActualSold: (key, val) => {
      setMonths(prev => {
        const parsed = parseInt(val) || 0;
        const patch = { ...prev[key], actualSold: parsed };
        // Auto-clear useActualSold when actual sold is emptied
        if (parsed <= 0) patch.useActualSold = false;
        const next = { ...prev, [key]: patch };
        save({ mo: next });
        return next;
      });
    },
    updateTargetNote: (key, note) => {
      setMonths(prev => {
        const next = { ...prev, [key]: { ...prev[key], targetNote: note || "" } };
        save({ mo: next });
        return next;
      });
    },
    updateVarField: (mKey, field, val) => {
      setMonths(prev => {
        const next = { ...prev, [mKey]: { ...prev[mKey], vars: { ...(prev[mKey]?.vars || {}), [field]: parseFloat(val) || 0 } } };
        save({ mo: next });
        return next;
      });
    },
    resetVars: (mKey) => {
      setMonths(prev => {
        const next = { ...prev, [mKey]: { ...prev[mKey], vars: undefined } };
        save({ mo: next });
        return next;
      });
    },
    updateUnitsOverride: (key, val) => {
      const todayStr = `${TODAY.getFullYear()}-${TODAY.getMonth() + 1}-${TODAY.getDate()}`;
      setMonths(prev => {
        const next = { ...prev, [key]: { ...prev[key], unitsOverride: val === "" ? null : parseInt(val), unitsOverrideDate: val === "" ? null : todayStr } };
        save({ mo: next });
        return next;
      });
    },
    // Manual expenses
    addExpense: (mKey, exp) => {
      setMonths(prev => {
        const next = { ...prev, [mKey]: { ...prev[mKey], expenses: [...(prev[mKey]?.expenses || []), { id: uid(), ...exp }] } };
        save({ mo: next }); return next;
      });
    },
    deleteExpense: (mKey, id) => {
      setMonths(prev => {
        const next = { ...prev, [mKey]: { ...prev[mKey], expenses: (prev[mKey]?.expenses || []).filter(e => e.id !== id) } };
        save({ mo: next }); return next;
      });
    },
    toggleExpensePaid: (mKey, id) => {
      setMonths(prev => {
        const next = { ...prev, [mKey]: { ...prev[mKey], expenses: (prev[mKey]?.expenses || []).map(e => {
          if (e.id !== id) return e;
          const newPaid = !e.paid;
          return { ...e, paid: newPaid, paidAt: newPaid ? new Date().toISOString() : null };
        }) } };
        save({ mo: next }); return next;
      });
    },
    addIncomeItem: (mKey, item) => {
      setMonths(prev => {
        const next = { ...prev, [mKey]: { ...prev[mKey], incomeItems: [...(prev[mKey]?.incomeItems || []), { id: uid(), ...item }] } };
        save({ mo: next }); return next;
      });
    },
    deleteIncomeItem: (mKey, id) => {
      setMonths(prev => {
        const next = { ...prev, [mKey]: { ...prev[mKey], incomeItems: (prev[mKey]?.incomeItems || []).filter(e => e.id !== id) } };
        save({ mo: next }); return next;
      });
    },
    // Delivery overrides
    updateDeliveryOverride: (mKey, expId, patch) => {
      setMonths(prev => {
        // Auto-stamp paidAt when paid flips
        const prevOv = prev[mKey]?.deliveryOverrides?.[expId] || {};
        const mergedPatch = { ...patch };
        if (Object.prototype.hasOwnProperty.call(patch, "paid")) {
          mergedPatch.paidAt = patch.paid ? (patch.paidAt || new Date().toISOString()) : null;
        }
        const next = { ...prev, [mKey]: { ...prev[mKey], deliveryOverrides: { ...(prev[mKey]?.deliveryOverrides || {}), [expId]: { ...prevOv, ...mergedPatch } } } };
        save({ mo: next }); return next;
      });
    },
    // Order expense paid state
    toggleOrderExpPaid: (mKey, expId) => {
      setMonths(prev => {
        const cur = (prev[mKey]?.orderExpPaid || {})[expId] || false;
        const newPaid = !cur;
        const next = { ...prev, [mKey]: { ...prev[mKey],
          orderExpPaid: { ...(prev[mKey]?.orderExpPaid || {}), [expId]: newPaid },
          orderExpPaidAt: { ...(prev[mKey]?.orderExpPaidAt || {}), [expId]: newPaid ? new Date().toISOString() : null },
        } };
        save({ mo: next }); return next;
      });
    },
    setOrderExpPartial: (mKey, expId, val) => {
      setMonths(prev => {
        const next = { ...prev, [mKey]: { ...prev[mKey], orderExpPartial: { ...(prev[mKey]?.orderExpPartial || {}), [expId]: val } } };
        save({ mo: next }); return next;
      });
    },
    // Recurring overrides
    updateRecurringOverride: (mKey, recId, patch) => {
      setMonths(prev => {
        const prevOv = prev[mKey]?.recurringOverrides?.[recId] || {};
        const mergedPatch = { ...patch };
        if (Object.prototype.hasOwnProperty.call(patch, "paid")) {
          mergedPatch.paidAt = patch.paid ? (patch.paidAt || new Date().toISOString()) : null;
        }
        const next = { ...prev, [mKey]: { ...prev[mKey], recurringOverrides: { ...(prev[mKey]?.recurringOverrides || {}), [recId]: { ...prevOv, ...mergedPatch } } } };
        save({ mo: next }); return next;
      });
    },
    setDeliveryName: (mKey, name) => {
      setMonths(prev => {
        const next = { ...prev, [mKey]: { ...prev[mKey], deliveryName: name } };
        save({ mo: next }); return next;
      });
    },
  };

  // ── Recurring income ──
  const recurringIncomeOps = {
    add: (item = {}) => {
      const r = { id: uid(), name: "New Income", amount: 0, type: "calendar", isIncome: true, startDate: "", activeMonths: [1,2,3,4,5,6,7,8,9,10,11,12], activeYears: [], dayOfMonth: 1, intervalDays: 30, ...item };
      const u = [r, ...recurringIncome]; setRecurringIncome(u); save({ ri: u });
    },
    update: (id, patch) => {
      const u = recurringIncome.map(r => r.id === id ? { ...r, ...patch } : r);
      setRecurringIncome(u); save({ ri: u });
    },
    delete: (id) => {
      const u = recurringIncome.filter(r => r.id !== id);
      setRecurringIncome(u); save({ ri: u });
    },
  };

  // ── Recurring expenses ──
  const recurringExpenseOps = {
    add: (item = {}) => {
      // No category field — category is only for partners
      const r = { id: uid(), name: "New Expense", amount: 0, type: "calendar", isIncome: false, startDate: "", activeMonths: [1,2,3,4,5,6,7,8,9,10,11,12], activeYears: [], dayOfMonth: 1, intervalDays: 30, ...item };
      const u = [r, ...recurringExpenses]; setRecurringExpenses(u); save({ re: u });
    },
    update: (id, patch) => {
      const u = recurringExpenses.map(r => r.id === id ? { ...r, ...patch } : r);
      setRecurringExpenses(u); save({ re: u });
    },
    delete: (id) => {
      const u = recurringExpenses.filter(r => r.id !== id);
      setRecurringExpenses(u); save({ re: u });
    },
    setAll: (u) => { setRecurringExpenses(u); save({ re: u }); },
  };

  // ── Orders ──
  const orderOps = {
    add: (initialData = {}) => {
      const o = {
        id: uid(), number: `O${orders.length + 1}`, orderDate: "", amount: 0,
        containerLines: [{ id: uid(), type: "40hc", qty: 0, cbmOverride: null }],
        shippingCostPerContainer: 0, shippingPaymentDays: 0,
        paymentTerms: [{ id: uid(), label: "1st payment", pct: 100, date: "", triggerType: "order_date", daysAfter: 0 }],
        containerBatches: [],
        ...initialData,
      };
      const u = [...orders, o]; setOrders(u); save({ ord: u });
      return o.id;
    },
    update: (id, patch) => {
      const u = orders.map(o => o.id === id ? { ...o, ...patch } : o);
      setOrders(u); save({ ord: u });
    },
    delete: (id) => {
      const u = orders.filter(o => o.id !== id);
      setOrders(u); save({ ord: u });
    },
    addContainerLine: (orderId) => {
      const u = orders.map(o => o.id === orderId ? { ...o, containerLines: [...(o.containerLines || []), { id: uid(), type: "40hc", qty: 0, cbmOverride: null }] } : o);
      setOrders(u); save({ ord: u });
    },
    updateContainerLine: (orderId, lineId, patch) => {
      const u = orders.map(o => o.id === orderId ? { ...o, containerLines: (o.containerLines || []).map(l => l.id === lineId ? { ...l, ...patch } : l) } : o);
      setOrders(u); save({ ord: u });
    },
    deleteContainerLine: (orderId, lineId) => {
      const u = orders.map(o => o.id === orderId ? { ...o, containerLines: (o.containerLines || []).filter(l => l.id !== lineId) } : o);
      setOrders(u); save({ ord: u });
    },
    addPaymentTerm: (orderId) => {
      const u = orders.map(o => o.id === orderId ? { ...o, paymentTerms: [...o.paymentTerms, { id: uid(), label: `Payment ${o.paymentTerms.length + 1}`, pct: 0, date: "", triggerType: "days_after_arrival", daysAfter: 0 }] } : o);
      setOrders(u); save({ ord: u });
    },
    updatePaymentTerm: (orderId, ptId, patch) => {
      const u = orders.map(o => o.id === orderId ? { ...o, paymentTerms: o.paymentTerms.map(pt => pt.id === ptId ? { ...pt, ...patch } : pt) } : o);
      setOrders(u); save({ ord: u });
    },
    deletePaymentTerm: (orderId, ptId) => {
      const u = orders.map(o => o.id === orderId ? { ...o, paymentTerms: o.paymentTerms.filter(pt => pt.id !== ptId) } : o);
      setOrders(u); save({ ord: u });
    },
    addContainerBatch: (orderId) => {
      const u = orders.map(o => o.id === orderId ? { ...o, containerBatches: [...o.containerBatches, { id: uid(), arrivalDate: "", departureDate: "", containers: 0, paymentTermIdx: 1, customAmounts: {}, shippingCostOverride: null, customTaxes: null }] } : o);
      setOrders(u); save({ ord: u });
    },
    updateContainerBatch: (orderId, bId, patch) => {
      const u = orders.map(o => o.id === orderId ? { ...o, containerBatches: o.containerBatches.map(b => b.id === bId ? { ...b, ...patch } : b) } : o);
      setOrders(u); save({ ord: u });
    },
    deleteContainerBatch: (orderId, bId) => {
      const u = orders.map(o => o.id === orderId ? { ...o, containerBatches: o.containerBatches.filter(b => b.id !== bId) } : o);
      setOrders(u); save({ ord: u });
    },
  };

  // ── Simple setters with save ──
  function setAvailableToday(v, reason) {
    const prev = availableToday;
    setAvailableTodayState(v);
    // Log to history
    const entry = {
      date: new Date().toISOString(),
      previousValue: Math.round(prev * 100) / 100,
      newValue: Math.round(v * 100) / 100,
      delta: Math.round((v - prev) * 100) / 100,
      reason: reason || "Manual edit",
    };
    const history = [entry, ...(availableTodayHistory || [])];
    if (history.length > 200) history.length = 200;
    setAvailableTodayHistoryState(history);
    save({ at: v, ath: history });
  }
  // Legacy setInitialStock removed — inventory is now tracked exclusively
  // through pools, marketplace stock, and supplier orders.
  function setDeliveryCreditLimit(v) { setDeliveryCreditLimitState(v); save({ dcl: v }); }
  function setVisibleYears(v) { setVisibleYearsState(v); save({ vy: v }); }
  function setFbmSettings(v) { setFbmSettingsState(v); save({ fbm: v }); }
  function setPartners(v) { setPartnersState(v); save({ par: v }); }
  function setModifiers(v) { setModifiersState(v); save({ mod: v }); }
  function setDecisions(v) { setDecisionsState(v); save({ dec: v }); }
  function setTaxes(v) { setTaxesState(v); save({ tax: v }); }
  function setIncomeOverride(key, val) {
    const u = { ...incomeOverrides, [key]: val === "" ? null : parseFloat(val) };
    setIncomeOverrides(u); save({ io: u });
  }
  function clearIncomeOverride(key) {
    const u = { ...incomeOverrides, [key]: null };
    setIncomeOverrides(u); save({ io: u });
  }

  // ── Inventory Pool operations ──
  const poolOps = {
    add: (name = "New Pool") => {
      const id = "pool_" + Math.random().toString(36).slice(2, 10);
      const pool = { id, name, marketplaceIds: [], initialStock: 0, initialCostPerUnit: 0, initialStockDate: "", initialStockHistory: [], orderIds: [] };
      const next = [...inventoryPools, pool];
      setInventoryPoolsState(next); save({ ipools: next });
      return pool;
    },
    update: (id, patch) => {
      // Enforce: a marketplace can only belong to ONE pool. Strip any
      // marketplace IDs that already exist in a different pool.
      if (Array.isArray(patch.marketplaceIds)) {
        const otherPoolMkts = new Set();
        for (const p of inventoryPools) {
          if (p.id === id) continue;
          for (const mid of (p.marketplaceIds || [])) otherPoolMkts.add(mid);
        }
        patch = { ...patch, marketplaceIds: patch.marketplaceIds.filter(mid => !otherPoolMkts.has(mid)) };
      }
      const next = inventoryPools.map(p => p.id === id ? { ...p, ...patch } : p);
      setInventoryPoolsState(next); save({ ipools: next });
    },
    delete: (id) => {
      const next = inventoryPools.filter(p => p.id !== id);
      setInventoryPoolsState(next); save({ ipools: next });
    },
    setStock: (poolId, units, costPerUnit, reason) => {
      const next = inventoryPools.map(p => {
        if (p.id !== poolId) return p;
        const now = new Date();
        const dateStr = `${String(now.getDate()).padStart(2,"0")}.${String(now.getMonth()+1).padStart(2,"0")}.${now.getFullYear()}`;
        const entry = { date: now.toISOString(), previousUnits: p.initialStock, newUnits: units, previousCost: p.initialCostPerUnit, newCost: costPerUnit, reason: reason || "Manual edit" };
        const history = [entry, ...(p.initialStockHistory || [])].slice(0, 200);
        return { ...p, initialStock: units, initialCostPerUnit: costPerUnit, initialStockDate: p.initialStockDate || dateStr, initialStockHistory: history };
      });
      setInventoryPoolsState(next); save({ ipools: next });
    },
    setAll: (pools) => { setInventoryPoolsState(pools); save({ ipools: pools }); },
  };

  return {
    // State
    marketplaces, months, recurringIncome, recurringExpenses,
    deliveryCreditLimit, orders, fbmSettings, partners, modifiers, decisions, taxes,
    profitCards,
    incomeOverrides, availableToday, availableTodayHistory,
    initialStock, initialCostPerUnit, initialStockDate, initialStockHistory,
    inventoryPools,
    visibleYears,
    saveStatus, currency, unitSystem, dataLoading, pastBalances,
    loadError,   // expose so the app can render a Retry card

    // Operations
    mktOps, monthOps, recurringIncomeOps, recurringExpenseOps, orderOps, poolOps,

    // Simple setters
    setAvailableToday, setDeliveryCreditLimit,
    setVisibleYears, setFbmSettings, setPartners, setModifiers, setDecisions,
    setProfitCards: (v) => { setProfitCardsState(v); save({ pc: v }); },
    setIncomeOverride, clearIncomeOverride,

    // Settings setters
    setCurrency: (v) => { setCurrencyState(v); save({ cur: v }); },
    setUnitSystem: (v) => { setUnitSystemState(v); save({ us: v }); },
    weekStartDay, setWeekStartDay: (v) => { setWeekStartDayState(v); save({ wsd: v }); },

    // Direct state setters (for complex updates)
    setMonths, save,

    // Force a fresh fetch of brand state from the server (post-OAuth, post-sync, etc.)
    reload,
  };
}

// ─── Auth hook ────────────────────────────────────────────────────────────────
export function useAuth() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });
    // Suppress spurious TOKEN_REFRESHED when tab regains focus — prevents full reload
    let suppressNext = false;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") suppressNext = true;
      else setTimeout(() => { suppressNext = false; }, 2000);
    };
    document.addEventListener("visibilitychange", onVisibility);

    const { data: { subscription: sub } } = supabase.auth.onAuthStateChange((event, s) => {
      if (suppressNext && (event === "TOKEN_REFRESHED" || event === "SIGNED_IN")) return;
      if (event === "PASSWORD_RECOVERY") {
        setPasswordRecovery(true);
      }
      setSession(s);
    });
    return () => {
      sub.unsubscribe();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!session) { setSubLoading(false); return; }
    setSubLoading(true);
    supabase.from("subscriptions").select("*").eq("user_id", session.user.id).maybeSingle()
      .then(({ data }) => {
        setSubscription(data || null);
        setSubLoading(false);
      })
      .catch(() => setSubLoading(false));
  }, [session?.user?.id]);

  async function signOut() {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error("Sign out error:", err);
    } finally {
      setSession(null);
      setSubscription(null);
    }
  }

  return { session, authLoading, subLoading, subscription, signOut, passwordRecovery, setPasswordRecovery };
}
